import { useState, useEffect, useCallback } from 'react';
import { Package, MapPin, ArrowRightLeft, AlertTriangle, Plus, Search, RefreshCw, ChevronRight, Minus, TrendingUp } from 'lucide-react';
import { useSalonId } from '../../hooks/useSalonId';
import { inventoryApi, type ProductResponse, type LocationResponse, type InventoryStatsResponse, type LowStockProductResponse } from '../../api/inventoryApi';
import ProductModal from './ProductModal';
import LocationModal from './LocationModal';
import TransferModal from './TransferModal';
import StockOperationModal from './StockOperationModal';

const InventoryPage = () => {
    const salonId = useSalonId();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    // Data states
    const [stats, setStats] = useState<InventoryStatsResponse | null>(null);
    const [products, setProducts] = useState<ProductResponse[]>([]);
    const [locations, setLocations] = useState<LocationResponse[]>([]);
    const [lowStockProducts, setLowStockProducts] = useState<LowStockProductResponse[]>([]);

    // UI states
    const [activeTab, setActiveTab] = useState<'products' | 'locations'>('products');
    const [expandedProduct, setExpandedProduct] = useState<string | null>(null);

    // Modal states
    const [showProductModal, setShowProductModal] = useState(false);
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [showStockModal, setShowStockModal] = useState(false);
    const [stockOperation, setStockOperation] = useState<'add' | 'remove'>('add');
    const [editingProduct, setEditingProduct] = useState<ProductResponse | null>(null);
    const [editingLocation, setEditingLocation] = useState<LocationResponse | null>(null);

    // Load all data
    const loadData = useCallback(async () => {
        if (!salonId) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const [statsData, productsData, locationsData, lowStockData] = await Promise.all([
                inventoryApi.getInventoryStats(salonId),
                inventoryApi.getProducts(salonId),
                inventoryApi.getLocations(salonId),
                inventoryApi.getLowStockProducts(salonId)
            ]);

            setStats(statsData);
            setProducts(Array.isArray(productsData) ? productsData : []);
            setLocations(Array.isArray(locationsData) ? locationsData : []);
            setLowStockProducts(Array.isArray(lowStockData) ? lowStockData : []);
        } catch (err) {
            console.error('Error loading inventory data:', err);
            setError('Error al cargar el inventario');
        } finally {
            setIsLoading(false);
        }
    }, [salonId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleRefresh = () => {
        loadData();
    };

    const handleModalSuccess = () => {
        loadData(); // Refresh data after any modal action
    };

    // Open modals
    const openProductModal = (product?: ProductResponse) => {
        setEditingProduct(product || null);
        setShowProductModal(true);
    };

    const openLocationModal = (location?: LocationResponse) => {
        setEditingLocation(location || null);
        setShowLocationModal(true);
    };

    const openStockModal = (operation: 'add' | 'remove') => {
        setStockOperation(operation);
        setShowStockModal(true);
    };

    const formatCurrency = (value: number | null | undefined) => {
        if (value == null || isNaN(value)) return '$ 0';
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const filteredProducts = products.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.sku.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (product.category && product.category.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    // Empty state when no salon
    if (!salonId) {
        return (
            <div className="space-y-6">
                <h1 className="text-2xl font-bold text-gray-900">Inventario</h1>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
                    <Package className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-4 text-lg font-semibold text-gray-900">Sin inventario</h3>
                    <p className="mt-2 text-gray-500">Configura tu salón para gestionar el inventario.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Inventario</h1>
                    <p className="text-gray-500">
                        Gestión de productos y ubicaciones
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={handleRefresh}
                        disabled={isLoading}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 cursor-pointer"
                        title="Refrescar"
                    >
                        <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                    </button>

                    <button
                        onClick={() => openProductModal()}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
                    >
                        <Plus className="w-4 h-4" />
                        <span>Nuevo Producto</span>
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-600">{error}</p>
                </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Total Products */}
                <div
                    className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setActiveTab('products')}
                >
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium text-gray-500">Total Productos</h3>
                        <div className="p-2 bg-blue-50 rounded-lg">
                            <Package className="w-5 h-5 text-blue-600" />
                        </div>
                    </div>
                    {isLoading ? (
                        <div className="animate-pulse h-8 bg-gray-200 rounded w-16"></div>
                    ) : (
                        <p className="text-2xl font-bold text-gray-900">{stats?.totalProducts ?? 0}</p>
                    )}
                </div>

                {/* Total Locations */}
                <div
                    className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setActiveTab('locations')}
                >
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium text-gray-500">Ubicaciones</h3>
                        <div className="p-2 bg-purple-50 rounded-lg">
                            <MapPin className="w-5 h-5 text-purple-600" />
                        </div>
                    </div>
                    {isLoading ? (
                        <div className="animate-pulse h-8 bg-gray-200 rounded w-16"></div>
                    ) : (
                        <p className="text-2xl font-bold text-gray-900">{stats?.totalLocations ?? 0}</p>
                    )}
                </div>

                {/* Inventory Value */}
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-xl shadow-lg text-white">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium opacity-90">Valor del Inventario</h3>
                        <TrendingUp className="w-5 h-5 opacity-80" />
                    </div>
                    {isLoading ? (
                        <div className="animate-pulse h-8 bg-white/20 rounded w-32"></div>
                    ) : (
                        <p className="text-3xl font-bold">{formatCurrency(stats?.totalInventoryValue)}</p>
                    )}
                </div>

                {/* Low Stock Alerts */}
                <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium text-gray-500">Stock Bajo</h3>
                        <div className={`p-2 rounded-lg ${(stats?.lowStockCount ?? 0) > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                            <AlertTriangle className={`w-5 h-5 ${(stats?.lowStockCount ?? 0) > 0 ? 'text-red-600' : 'text-green-600'}`} />
                        </div>
                    </div>
                    {isLoading ? (
                        <div className="animate-pulse h-8 bg-gray-200 rounded w-16"></div>
                    ) : (
                        <p className={`text-2xl font-bold ${(stats?.lowStockCount ?? 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {stats?.lowStockCount ?? 0}
                        </p>
                    )}
                </div>
            </div>

            {/* Quick Actions Bar */}
            <div className="flex flex-wrap gap-3">
                <button
                    onClick={() => openStockModal('add')}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors border border-emerald-200 cursor-pointer"
                >
                    <Plus className="w-4 h-4" />
                    <span className="font-medium">Agregar Stock</span>
                </button>

                <button
                    onClick={() => openStockModal('remove')}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors border border-orange-200 cursor-pointer"
                >
                    <Minus className="w-4 h-4" />
                    <span className="font-medium">Retirar Stock</span>
                </button>

                <button
                    onClick={() => setShowTransferModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors border border-teal-200 cursor-pointer"
                >
                    <ArrowRightLeft className="w-4 h-4" />
                    <span className="font-medium">Transferir</span>
                </button>

                <button
                    onClick={() => openLocationModal()}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors border border-purple-200 cursor-pointer"
                >
                    <MapPin className="w-4 h-4" />
                    <span className="font-medium">Nueva Ubicación</span>
                </button>
            </div>

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Products & Locations Table */}
                <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                    {/* Tabs */}
                    <div className="flex border-b border-gray-100">
                        <button
                            onClick={() => setActiveTab('products')}
                            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${activeTab === 'products'
                                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <Package className="w-4 h-4 inline-block mr-2" />
                            Productos ({products.length})
                        </button>
                        <button
                            onClick={() => setActiveTab('locations')}
                            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${activeTab === 'locations'
                                ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50/50'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <MapPin className="w-4 h-4 inline-block mr-2" />
                            Ubicaciones ({locations.length})
                        </button>
                    </div>

                    {/* Search */}
                    {activeTab === 'products' && (
                        <div className="p-4 border-b border-gray-100">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Buscar por nombre, SKU o categoría..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                    )}

                    {/* Content */}
                    <div className="max-h-[500px] overflow-y-auto">
                        {isLoading ? (
                            <div className="p-8 text-center">
                                <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
                                <p className="text-gray-500">Cargando inventario...</p>
                            </div>
                        ) : activeTab === 'products' ? (
                            filteredProducts.length === 0 ? (
                                <div className="p-8 text-center">
                                    <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-500">No hay productos registrados</p>
                                    <p className="text-sm text-gray-400 mt-1">Agrega tu primer producto para comenzar</p>
                                    <button
                                        onClick={() => openProductModal()}
                                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
                                    >
                                        <Plus className="w-4 h-4 inline mr-2" />
                                        Crear Producto
                                    </button>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-100">
                                    {filteredProducts.map(product => (
                                        <div key={product.id} className="hover:bg-gray-50 transition-colors">
                                            <div
                                                className="p-4 flex items-center justify-between cursor-pointer"
                                                onClick={() => setExpandedProduct(expandedProduct === product.id ? null : product.id)}
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                                        <Package className="w-5 h-5 text-blue-600" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-900">{product.name}</p>
                                                        <p className="text-sm text-gray-500">SKU: {product.sku}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-6">
                                                    {product.category && (
                                                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                                            {product.category}
                                                        </span>
                                                    )}
                                                    <div className="text-right">
                                                        <p className={`font-semibold ${product.totalStock <= (product.minStockAlert ?? 5) ? 'text-red-600' : 'text-gray-900'}`}>
                                                            {product.totalStock} {product.unit}
                                                        </p>
                                                        <p className="text-sm text-gray-500">{formatCurrency(product.salePrice)}</p>
                                                    </div>
                                                    <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${expandedProduct === product.id ? 'rotate-90' : ''}`} />
                                                </div>
                                            </div>

                                            {/* Expanded details */}
                                            {expandedProduct === product.id && (
                                                <div className="px-4 pb-4 pt-0">
                                                    <div className="bg-gray-50 rounded-lg p-4">
                                                        <div className="flex items-center justify-between mb-3">
                                                            <p className="text-sm font-medium text-gray-700">Stock por ubicación:</p>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); openProductModal(product); }}
                                                                className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer"
                                                            >
                                                                Editar producto
                                                            </button>
                                                        </div>
                                                        {product.stockByLocation.length > 0 ? (
                                                            <div className="space-y-2">
                                                                {product.stockByLocation.map(stock => (
                                                                    <div key={stock.locationId} className="flex items-center justify-between text-sm">
                                                                        <div className="flex items-center gap-2">
                                                                            <MapPin className="w-4 h-4 text-purple-500" />
                                                                            <span className="text-gray-700">{stock.locationName}</span>
                                                                        </div>
                                                                        <span className="font-medium text-gray-900">{stock.quantity} {product.unit}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-gray-400 italic">Sin stock registrado aún</p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )
                        ) : (
                            locations.length === 0 ? (
                                <div className="p-8 text-center">
                                    <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-500">No hay ubicaciones registradas</p>
                                    <p className="text-sm text-gray-400 mt-1">Crea tu primera ubicación</p>
                                    <button
                                        onClick={() => openLocationModal()}
                                        className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors cursor-pointer"
                                    >
                                        <Plus className="w-4 h-4 inline mr-2" />
                                        Crear Ubicación
                                    </button>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-100">
                                    {locations.map(location => (
                                        <div
                                            key={location.id}
                                            className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                                            onClick={() => openLocationModal(location)}
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                                    <MapPin className="w-5 h-5 text-purple-600" />
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <p className="font-medium text-gray-900">{location.name}</p>
                                                        {location.isDefault && (
                                                            <span className="px-2 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full">
                                                                Principal
                                                            </span>
                                                        )}
                                                    </div>
                                                    {location.address && (
                                                        <p className="text-sm text-gray-500">{location.address}</p>
                                                    )}
                                                    {location.description && (
                                                        <p className="text-sm text-gray-400">{location.description}</p>
                                                    )}
                                                </div>
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-gray-400" />
                                        </div>
                                    ))}
                                </div>
                            )
                        )}
                    </div>
                </div>

                {/* Low Stock Alerts */}
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
                    <div className="p-4 border-b border-gray-100">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-amber-500" />
                            Alertas de Stock Bajo
                        </h3>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                        {isLoading ? (
                            <div className="p-8 text-center">
                                <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mx-auto" />
                            </div>
                        ) : lowStockProducts.length === 0 ? (
                            <div className="p-8 text-center">
                                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Package className="w-6 h-6 text-green-600" />
                                </div>
                                <p className="text-green-600 font-medium">¡Todo en orden!</p>
                                <p className="text-sm text-gray-500 mt-1">No hay productos con stock bajo</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {lowStockProducts.map(product => (
                                    <div key={product.productId} className="p-4 hover:bg-red-50/50 transition-colors">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="font-medium text-gray-900">{product.name}</p>
                                            <span className="text-red-600 font-bold">{product.currentStock}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-gray-500">{product.locationName}</span>
                                            <span className="text-gray-400">Min: {product.minStockAlert}</span>
                                        </div>
                                        <div className="mt-2 w-full bg-gray-100 rounded-full h-1.5">
                                            <div
                                                className="bg-red-500 h-1.5 rounded-full"
                                                style={{ width: `${Math.min((product.currentStock / product.minStockAlert) * 100, 100)}%` }}
                                            ></div>
                                        </div>
                                        <button
                                            onClick={() => openStockModal('add')}
                                            className="mt-3 w-full py-1.5 text-sm text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors font-medium cursor-pointer"
                                        >
                                            + Agregar Stock
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modals */}
            {salonId && (
                <>
                    <ProductModal
                        isOpen={showProductModal}
                        onClose={() => { setShowProductModal(false); setEditingProduct(null); }}
                        onSuccess={handleModalSuccess}
                        salonId={salonId}
                        locations={locations}
                        product={editingProduct}
                    />

                    <LocationModal
                        isOpen={showLocationModal}
                        onClose={() => { setShowLocationModal(false); setEditingLocation(null); }}
                        onSuccess={handleModalSuccess}
                        salonId={salonId}
                        location={editingLocation}
                    />

                    <TransferModal
                        isOpen={showTransferModal}
                        onClose={() => setShowTransferModal(false)}
                        onSuccess={handleModalSuccess}
                        salonId={salonId}
                        products={products}
                        locations={locations}
                    />

                    <StockOperationModal
                        isOpen={showStockModal}
                        onClose={() => setShowStockModal(false)}
                        onSuccess={handleModalSuccess}
                        salonId={salonId}
                        products={products}
                        locations={locations}
                        operation={stockOperation}
                    />
                </>
            )}
        </div>
    );
};

export default InventoryPage;
